import type { User } from "@auth0/auth0-react";
import { userService } from "../services/userService";

export const maskEmail = (email: string): string => {
	const [localPart, domain] = email.split("@");
	if (localPart.length <= 2) {
		return `${localPart[0]}***@${domain}`;
	}
	return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`;
};

export const maskPhoneNumber = (phoneNumber: string): string => {
	// Remove any spaces, dashes, or formatting
	const cleanPhone = phoneNumber.replace(/\D/g, "");

	if (cleanPhone.length <= 4) {
		return `${cleanPhone.slice(0, 2)}***`;
	}

	// Show first 3 digits and last 4 digits with *** in between
	// For example: +1234567890 -> +123***7890
	const firstPart = cleanPhone.slice(0, 3);
	const lastPart = cleanPhone.slice(-4);
	return `+${firstPart}***${lastPart}`;
};

export const isCodeComplete = (code: string[]): boolean => {
	return code.every((digit) => digit !== "");
};

export const getCodeString = (code: string[]): string => {
	return code.join("");
};

export interface TwoFactorDefaultMethodResult {
	defaultMethod: "phone" | "email";
	reason: "has_phone" | "no_phone" | "fallback_error";
	error?: string;
}

/**
 * Determines the default 2FA method based on user's phone number availability
 * Returns phone if user has a phone number, email otherwise
 */
export async function determineDefault2FAMethod(
	user: User | undefined,
	logUserAction: (action: string, metadata?: Record<string, unknown>) => void,
): Promise<TwoFactorDefaultMethodResult> {
	const userEmail = user?.email ? maskEmail(user.email) : "unknown";

	try {
		const defaultMethod = await userService.getDefault2FAMethod();
		const reason = defaultMethod === "phone" ? "has_phone" : "no_phone";

		// Analytics: Track default method selection
		logUserAction("2fa_default_method_determined", {
			defaultMethod,
			reason,
			userEmail,
		});

		return {
			defaultMethod,
			reason,
		};
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : "unknown";

		// Analytics: Track failure to determine default method
		logUserAction("2fa_default_method_fallback", {
			fallbackMethod: "email",
			error: errorMessage,
			userEmail,
		});

		return {
			defaultMethod: "email",
			reason: "fallback_error",
			error: errorMessage,
		};
	}
}
